import { effect, inject, Injectable, Signal, signal, untracked, WritableSignal } from '@angular/core';
import { LayoutService } from './layout.service';
import { BottomSheetService } from './components/bottom-sheet.service';
import { NavigationService } from './navigation.service';
import { BenefitsService } from './benefits.service';
import { MenuContainerService, MenuSelectionService } from '@services/menu';
import { VenueConfigurationsService } from './dvm/venue-configurations.service';
import { VenueLayoutContainerComponent } from '@components/minisite/topbar/venue-layout/venue-layout-container/venue-layout-container.component';

/**
 * Presentacion y conmutacion del panel VENUE LAYOUT. El estado vive en `VenueConfigurationsService`
 * (root); este tiene scope de vista porque usa menu, benefits y bottom sheet.
 */
@Injectable()
export class VenueLayoutService {

  // SERVICES
  private readonly bottomSheet:   BottomSheetService          = inject(BottomSheetService);
  private readonly navigation:    NavigationService           = inject(NavigationService);
  private readonly benefits:      BenefitsService             = inject(BenefitsService);
  private readonly menuContainer: MenuContainerService        = inject(MenuContainerService);
  private readonly menuSelection: MenuSelectionService        = inject(MenuSelectionService);
  public  readonly venueConfigs:  VenueConfigurationsService  = inject(VenueConfigurationsService);

  // GLOBAL STATE
  private readonly isMobile:      Signal<boolean>             = inject(LayoutService).isMobile;

  // COMPONENT STATE
  public  readonly showContainer: WritableSignal<boolean>     = signal(false);

  // ANIMATION STATE
  public  readonly isAnimating:   WritableSignal<boolean>     = signal(false);

  // METHODS
  public toggleContainer(): void {

    this.showContainer.update((value: boolean): boolean => !value);

    if(this.isMobile() && this.showContainer()){
      this.bottomSheet.open(VenueLayoutContainerComponent).close.subscribe((): void => this.showContainer.set(false));
      return;
    }

    // Al abrir en desktop, Menu y Benefits ceden el mismo hueco arriba-derecha.
    if(this.showContainer()){
      this.menuContainer.closeMenuContainer();
      this.benefits.showContainer.set(false);
    }

  }

  /**
   * El orden importa: limpiar antes de conmutar evita que `setAvailability` reaplique, durante la
   * carga del nuevo mapa, filtros con ids de nodo del venue anterior.
   */
  public selectConfiguration(id: string): void {

    // El panel sigue visible y clicable mientras se cierra: sin esto, un segundo click arrancaria
    // otra carga de mapa encima de la primera. `showContainer` cae sincrono, asi que no hay ventana.
    if(!this.showContainer()) return;

    this.menuSelection.clearCurrentSelection();
    this.menuContainer.closeMenuContainer();
    this.showContainer.set(false);

    // En mobile el panel vive en el bottom sheet: elegir debe cerrarlo.
    if(this.isMobile() && this.bottomSheet.state() !== 'closed') this.bottomSheet.close();

    // El historial guarda `map_id`s del venue anterior: sin reset, "atras" iria a un mapa inexistente.
    this.navigation.reset();

    this.venueConfigs.select(id);

  }

  /**
   * La superficie abierta debe corresponder al breakpoint: sheet en movil, panel en escritorio.
   * Se comprueba el estado, no la transicion: al coalescer el efecto no sabe que cambio primero.
   */
  private readonly reconcileOnBreakpoint = effect((): void => {

    const isMobile: boolean = this.isMobile();

    untracked((): void => {

      if(!this.showContainer()) return;

      // Solo el sheet propio: Menu y Benefits comparten la superficie y cierran la suya.
      const sheetOpen: boolean = this.bottomSheet.state() !== 'closed'
                              && this.bottomSheet.component() === VenueLayoutContainerComponent;

      // Panel de escritorio abierto en movil: el primer toque solo limpiaria este estado.
      if(isMobile && !sheetOpen) this.showContainer.set(false);

      // Sheet abierto en escritorio: se solaparia con el panel.
      if(!isMobile && sheetOpen) {
        this.showContainer.set(false);
        this.bottomSheet.close();
      }

    });

  });


  // Cierra este panel cuando se abre Menu o Benefits (solo desktop: en mobile no se solapan).
  // `showContainer()` se lee dentro de `untracked` o el efecto se dispararia a si mismo.
  private readonly closeOnOtherPanelOpened = effect((): void => {

    const otherOpened: boolean = this.benefits.showContainer() || !!this.menuContainer.showContainer();

    untracked((): void => {
      if(otherOpened && !this.isMobile() && this.showContainer()) this.showContainer.set(false);
    });

  });

}
